import * as THREE from 'three';
import { BLACK, cloth, emis, std } from '../materials.ts';
import { G, M, V, glowSprite } from '../prims.ts';
import { begin, bonesOf, done, joint, pointLight, type BaseOpts, type Model } from './types.ts';

/*
 * Oráculo de tinta (docs/CRIATURAS.md §2, era III): the host's warder. A tall, gaunt seer that floats a hand-span
 * off the ground, its ink-black robes running down into drips that pool beneath it. A tall pointed hood over a
 * faceless plate of ink-glass with one vertical eye weeping cold light; four thin bone-pale arms, the upper pair
 * holding up an orb of ink-glass with the light caged in it, the lower pair hanging out like a mantis's; a halo of
 * glass shards and glyphs turning slowly about its head. Its power, the `ward` clip, raises the orb and swells a
 * bubble of ink-glass out around it (the `ward` bone, built tiny so it hides inside the robe until a clip scales
 * it ×WARD_GROW); `crack` splits the bubble into two halves (`wardA`, `wardB`) that fall away.
 * Opaque shared materials for the bulk, one translucent (the ink-glass: orb and bubble) and one self-lit one
 * (the cold light: eye, tear, orb core, glyphs, the bubble's veins), plus glows.
 */

/** The cold light of the ward. */
const LIGHT = '#bff6ff';
/** How much the `ward` bone has to grow to show the bubble at full size (its geometry is built that much smaller). */
export const WARD_GROW = 50;
/** Places other code may want (model coordinates): the orb's centre at rest and the bubble's centre and radius. */
export const ORACLE = { orb: V(0, 1.47, 0.42), ward: V(0, 1.2, 0), wardR: 1.05 };

/**
 * A long bell of robe: an open cylinder whose wall folds in and out and whose hem runs down into points of ink
 * (every other column a long drip, the rest short).
 */
const bell = (rTop: number, rBot: number, h: number, seg: number, folds: number, drip: number, rng: () => number, gap = 0): THREE.BufferGeometry => {
  const geo = new THREE.CylinderGeometry(rTop, rBot, h, seg, 4, true, gap / 2, Math.PI * 2 - gap);
  const p = geo.attributes.position as THREE.BufferAttribute;
  const cuts = new Map<number, number>();
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
    const a = Math.atan2(x, z);
    const k = (h / 2 - y) / h;
    const f = 1 + 0.08 * k * Math.sin(a * folds + 0.3) + 0.035 * k * Math.sin(a * folds * 2.4 + 1);
    p.setX(i, x * f);
    p.setZ(i, z * f);
    if (k > 0.99) {
      const key = Math.round(a * 1000);
      let cut = cuts.get(key);
      if (cut === undefined) {
        cut = cuts.size % 2 ? -drip * (0.7 + rng() * 0.6) : drip * 0.15 * rng();
        cuts.set(key, cut);
      }
      // The long drips pull in a little as they run down (wet cloth narrows into a drop).
      const pull = cut < 0 ? 0.88 : 1;
      p.setXYZ(i, x * f * pull, y + cut, z * f * pull);
    }
  }
  geo.computeVertexNormals();
  return geo;
};

/** A zigzag of points along a meridian of a sphere (centre `c`, radius `r`), from polar angle t0 to t1 at azimuth `a`. */
const vein = (c: THREE.Vector3, r: number, a: number, t0: number, t1: number, n: number, rng: () => number, jag = 0.12): THREE.Vector3[] => {
  const pts: THREE.Vector3[] = [];
  for (let i = 0; i <= n; i++) {
    const t = t0 + ((t1 - t0) * i) / n;
    const aa = a + (i === 0 || i === n ? 0 : (rng() - 0.5) * 2 * jag);
    pts.push(V(c.x + r * Math.sin(t) * Math.sin(aa), c.y + r * Math.cos(t), c.z + r * Math.sin(t) * Math.cos(aa)));
  }
  return pts;
};

/** An open tapered tube from a (radius r1) to b (r2): `limb` without the caps, for the thin parts. */
const rod = (parent: THREE.Object3D, a: THREE.Vector3, b: THREE.Vector3, r1: number, r2: number, mat: THREE.Material, seg = 4): THREE.Mesh => {
  const v = new THREE.Vector3().subVectors(b, a);
  const m = M(new THREE.CylinderGeometry(r2, r1, v.length(), seg, 1, true), mat, parent);
  m.position.copy(a).addScaledVector(v, 0.5);
  m.quaternion.setFromUnitVectors(V(0, 1, 0), v.normalize());
  return m;
};

/** Thin straight segments through the points (a crack or a vein of light). */
const polyline = (parent: THREE.Object3D, pts: THREE.Vector3[], r: number, mat: THREE.Material): void => {
  for (let i = 1; i < pts.length; i++) rod(parent, pts[i - 1] as THREE.Vector3, pts[i] as THREE.Vector3, r, r, mat, 3);
};

/**
 * Bones: body (the floating figure, pivot at the waist) carries the skirt (`robe`, which trails and drips) and the
 * chest; the chest carries the head (and on it the turning `halo`), the upper pair of arms (armL/foreL, armR/foreR)
 * that hold the `orb` (a bone of its own on the chest, so a clip can raise it), and the lower pair (arm2L/fore2L,
 * arm2R/fore2R), the clawing ones. `ward` rides the model group (the bubble, hidden at rest) with its two halves
 * `wardA` (left) and `wardB` (right) hinged at its foot. The ink pool stays on the ground (on the model group).
 */
export const oracle = (o: BaseOpts = {}): Model => {
  const c = begin(o, 0.5, 61);
  const { g, R, rng } = c;
  const robeM = std('#161c28', { roughness: 0.26, metalness: 0.35, bumpScale: 0.014 });
  const robeIn = cloth('#2a3345', { side: THREE.DoubleSide });
  const hoodM = std('#161c28', { roughness: 0.26, metalness: 0.35, bumpScale: 0.014, side: THREE.DoubleSide });
  const ink = std('#05070b', { roughness: 0.08, metalness: 0.45, bumpScale: 0 });
  const plate = std('#1b2531', { roughness: 0.12, metalness: 0.6, bumpScale: 0 });
  const bone = std('#c3c9d1', { roughness: 0.55, bumpScale: 0.008 });
  const claw = std('#e4e8ee', { roughness: 0.35 });
  const stained = std('#6f7784', { roughness: 0.4, metalness: 0.2, bumpScale: 0.008 });
  const light = emis('#d8fbff', '#79e6ff', 1.7);
  const glass = std('#8fdcf2', { transparent: true, opacity: 0.26, depthWrite: false, roughness: 0.06, metalness: 0.4, bumpScale: 0, side: THREE.DoubleSide, emissive: '#1c5468', emissiveIntensity: 0.55 });

  const bodyJ = joint(g, V(0, 1.0, 0), 'body');
  const robeJ = joint(bodyJ.abs, V(0, 1.02, 0), 'robe');
  const chestJ = joint(bodyJ.abs, V(0, 1.14, 0), 'chest');
  const ch = chestJ.abs;
  const headJ = joint(ch, V(0, 1.66, 0.06), 'head');
  const haloJ = joint(headJ.abs, V(0, 2.0, 0.02), 'halo');
  haloJ.p.rotation.x = -0.32;
  const shL = V(-0.15, 1.53, 0.0), elL = V(-0.28, 1.3, 0.16), hL = V(-0.085, 1.41, 0.42);
  const shR = V(0.15, 1.53, 0.0), elR = V(0.28, 1.3, 0.16), hR = V(0.085, 1.41, 0.42);
  const sh2L = V(-0.14, 1.36, 0.02), el2L = V(-0.42, 1.44, -0.02), h2L = V(-0.47, 0.9, 0.2);
  const sh2R = V(0.14, 1.36, 0.02), el2R = V(0.42, 1.44, -0.02), h2R = V(0.47, 0.9, 0.2);
  const armLJ = joint(ch, shL, 'armL');
  const foreLJ = joint(armLJ.abs, elL, 'foreL');
  const armRJ = joint(ch, shR, 'armR');
  const foreRJ = joint(armRJ.abs, elR, 'foreR');
  const arm2LJ = joint(ch, sh2L, 'arm2L');
  const fore2LJ = joint(arm2LJ.abs, el2L, 'fore2L');
  const arm2RJ = joint(ch, sh2R, 'arm2R');
  const fore2RJ = joint(arm2RJ.abs, el2R, 'fore2R');
  const O = ORACLE.orb;
  const orbJ = joint(ch, O, 'orb');
  const W = ORACLE.ward;
  const wardJ = joint(g, W, 'ward');

  // The pool of ink it leaves under itself, wet and black, with a few spatters.
  const pg = new THREE.CircleGeometry(0.42, 18);
  const pp = pg.attributes.position as THREE.BufferAttribute;
  for (let i = 1; i < pp.count; i++) {
    const f = 0.82 + 0.2 * Math.sin(i * 2.3) + rng() * 0.18;
    pp.setXY(i, pp.getX(i) * f, pp.getY(i) * f);
  }
  const pool = M(pg, ink, g);
  pool.rotation.x = -Math.PI / 2;
  pool.position.set(0, 0.006, 0.02);
  for (let i = 0; i < 5; i++) {
    const a = rng() * 6.3;
    const d = M(new THREE.CircleGeometry(0.03 + rng() * 0.03, 7), ink, g);
    d.rotation.x = -Math.PI / 2;
    d.position.set(Math.cos(a) * (0.45 + rng() * 0.12), 0.005, Math.sin(a) * (0.45 + rng() * 0.12));
  }
  // Drops falling, a ripple ring where they land.
  const rip = M(new THREE.TorusGeometry(0.1, 0.006, 3, 8), ink, g);
  rip.rotation.x = Math.PI / 2;
  rip.position.set(0.05, 0.01, 0.06);

  // The skirt: a long wet bell from the waist down to a hem that runs into drips, floating clear of the pool.
  const rb = robeJ.abs;
  const skirt = M(bell(0.15, 0.31, 0.64, 13, 7, 0.12, rng, 0.75), robeM, rb);
  skirt.position.set(0, 0.76, 0);
  // An inner layer, slate, showing at the slits.
  const under = M(bell(0.12, 0.26, 0.6, 9, 5, 0.12, rng), robeIn, rb);
  under.position.set(0, 0.74, 0.0);
  under.rotation.y = 0.35;
  // Tendrils of ink running off the hem down to the pool, each ending in a falling drop.
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2 + 0.25 + R() * 0.3;
    const r0 = 0.25 + R() * 0.04;
    const top = V(Math.sin(a) * r0, 0.44 + R() * 0.04, Math.cos(a) * r0);
    const mid = V(Math.sin(a) * (r0 - 0.05), 0.26 + R() * 0.05, Math.cos(a) * (r0 - 0.05));
    const end = V(Math.sin(a) * (r0 - 0.06) + R() * 0.03, 0.08 + rng() * 0.1, Math.cos(a) * (r0 - 0.06));
    rod(rb, top, mid, 0.026, 0.013, ink, 5);
    rod(rb, mid, end, 0.013, 0.003, ink, 4);
    const dr = M(new THREE.SphereGeometry(0.013, 4, 3), ink, rb);
    dr.position.copy(end).add(V(0, -0.025 - rng() * 0.02, 0));
    dr.scale.y = 1.5;
  }
  // Two drops that fall from the hem to the pool (the idle clip drops them over and over).
  const drips = [joint(rb, V(0.15, 0.13, 0.15), 'drip'), joint(rb, V(-0.17, 0.15, -0.08), 'drip2')];
  for (const d of drips) {
    const at = d.p.position;
    const a = M(new THREE.SphereGeometry(0.016, 5, 3), ink, d.abs);
    a.position.copy(at);
    a.scale.y = 1.8;
  }
  // A long stole down the front, sewn with glyphs of the cold light.
  const stole = M(new THREE.BoxGeometry(0.075, 0.6, 0.012), plate, rb);
  stole.position.set(0, 0.7, 0.215);
  stole.rotation.x = -0.18;
  for (let i = 0; i < 5; i++) {
    const s = M(new THREE.OctahedronGeometry(0.014, 0), light, rb);
    s.position.set(0, 0.93 - i * 0.12, 0.226 + i * 0.022);
    s.scale.set(0.7, 1.4, 0.4);
  }

  // The gaunt trunk: narrow, long, hunched a little forward, under a stiff mantle of ink that falls in points.
  const torso = M(new THREE.SphereGeometry(0.15, 10, 8), robeM, ch);
  torso.scale.set(1.0, 1.75, 0.78);
  torso.position.set(0, 1.36, 0.0);
  torso.rotation.x = 0.12;
  const coat = M(bell(0.11, 0.19, 0.5, 10, 5, 0.06, rng), robeM, ch);
  coat.position.set(0, 1.28, 0.0);
  const mantle = M(bell(0.07, 0.34, 0.36, 12, 6, 0.2, rng), robeM, ch);
  mantle.position.set(0, 1.5, 0.02);
  mantle.rotation.x = 0.1;
  // A cage of bone at the throat: a collar of thin ribs closing over the chest.
  for (let i = 0; i < 2; i++) {
    const r = M(new THREE.TorusGeometry(0.1 + i * 0.014, 0.007, 3, 8, Math.PI * 0.9), bone, ch);
    r.position.set(0, 1.52 - i * 0.06, 0.06 + i * 0.014);
    r.rotation.set(0.35, 0, Math.PI * 1.05);
  }
  M(new THREE.OctahedronGeometry(0.03, 0), light, ch).position.set(0, 1.4, 0.18);
  glowSprite(ch, '#5fd8ff', 0.22, V(0, 1.4, 0.2), 0.7);
  rod(ch, V(0, 1.55, 0.02), V(0, 1.72, 0.09), 0.045, 0.035, BLACK, 5);

  // The head: a tall hood with its point bent back, a void inside, a faceless plate of ink-glass and one eye.
  const hd = G(headJ.abs);
  const prof: THREE.Vector2[] = [];
  for (let i = 0; i <= 9; i++) {
    const k = i / 9;
    const y = 1.6 + k * 0.62;
    const r = k < 0.35 ? 0.15 + 0.03 * Math.sin((k / 0.35) * Math.PI * 0.5) : 0.18 * (1 - (k - 0.35) / 0.65) ** 1.2 + 0.004;
    prof.push(new THREE.Vector2(r, y));
  }
  const hg = new THREE.LatheGeometry(prof, 12, 0.75, Math.PI * 2 - 1.5);
  const hp = hg.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < hp.count; i++) {
    const y = hp.getY(i);
    // The point swept back, the cowl drawn forward over the face.
    const up = Math.max(0, y - 1.88);
    hp.setZ(i, hp.getZ(i) - 2.4 * up * up + (y < 1.85 ? 0.04 : 0));
  }
  hg.computeVertexNormals();
  M(hg, hoodM, hd);
  const voidM = M(new THREE.SphereGeometry(0.13, 6, 4), BLACK, hd);
  voidM.position.set(0, 1.8, 0.04);
  voidM.scale.set(1, 1.3, 1);
  // The face: a long smooth plate of ink-glass, no mouth, no nose.
  const face = M(new THREE.SphereGeometry(0.085, 7, 6, Math.PI / 2 - 1.1, 2.2, 0.2, Math.PI - 0.5), plate, hd);
  face.scale.set(0.8, 1.5, 0.75);
  face.position.set(0, 1.8, 0.1);
  face.rotation.x = 0.1;
  // The eye: a vertical slit of cold light set in a black lid, weeping light down the plate.
  const lid = M(new THREE.SphereGeometry(0.022, 6, 4), BLACK, hd);
  lid.scale.set(0.75, 2.2, 0.5);
  lid.position.set(0, 1.86, 0.162);
  const eye = M(new THREE.SphereGeometry(0.014, 6, 4), light, hd);
  eye.scale.set(0.6, 2.6, 0.6);
  eye.position.set(0, 1.86, 0.17);
  glowSprite(hd, LIGHT, 0.12, V(0, 1.86, 0.19), 0.95);
  glowSprite(hd, '#5fd8ff', 0.3, V(0, 1.84, 0.18), 0.35);
  polyline(hd, [V(0, 1.81, 0.168), V(0.006, 1.77, 0.166), V(0.002, 1.72, 0.158), V(0.007, 1.67, 0.142)], 0.0042, light);
  const tear = M(new THREE.SphereGeometry(0.009, 5, 4), light, hd);
  tear.position.set(0.007, 1.655, 0.14);
  tear.scale.y = 1.6;
  // Hairline cracks in the plate.
  polyline(hd, [V(-0.01, 1.93, 0.16), V(-0.03, 1.89, 0.158), V(-0.045, 1.84, 0.148)], 0.0025, BLACK);
  polyline(hd, [V(0.02, 1.76, 0.165), V(0.04, 1.73, 0.152)], 0.0025, BLACK);

  // The halo: shards of ink-glass and glyph-slivers of light on a faint ring, turning about the head.
  const hl = haloJ.abs;
  const HC = V(0, 2.0, 0.02);
  const ringH = M(new THREE.TorusGeometry(0.25, 0.0035, 3, 16), light, hl);
  ringH.rotation.x = Math.PI / 2;
  ringH.position.copy(HC);
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2;
    const lit = i % 3 === 0;
    const r = 0.25 + (lit ? 0 : 0.015 * Math.sin(i * 1.7));
    const s = M(new THREE.OctahedronGeometry(lit ? 0.022 : 0.028, 0), lit ? light : plate, hl);
    s.position.set(HC.x + Math.sin(a) * r, HC.y + 0.02 * Math.sin(i * 2.1), HC.z + Math.cos(a) * r);
    s.scale.set(0.45, lit ? 1.9 : 2.6, 0.45);
    s.rotation.set(R() * 0.5, a, R() * 0.6);
    if (!lit && i % 2) {
      const d = M(new THREE.SphereGeometry(0.012, 4, 3), ink, hl);
      d.position.set(HC.x + Math.sin(a + 0.33) * r, HC.y - 0.01, HC.z + Math.cos(a + 0.33) * r);
    }
  }
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    glowSprite(hl, '#7fe8ff', 0.09, V(HC.x + Math.sin(a) * 0.25, HC.y, HC.z + Math.cos(a) * 0.25), 0.8);
  }

  // An arm: a sleeve of ink to the elbow (a cuff of it hanging), then a long bare forearm of pale bone.
  const sleeve = (arm: THREE.Object3D, fore: THREE.Object3D, s: THREE.Vector3, e: THREE.Vector3, h: THREE.Vector3): void => {
    rod(arm, s, e, 0.04, 0.028, robeM, 6);
    rod(fore, e, e.clone().lerp(h, 0.45), 0.02, 0.017, stained, 5);
    rod(fore, e.clone().lerp(h, 0.45), h, 0.017, 0.013, bone, 5);
    M(new THREE.SphereGeometry(0.024, 5, 4), bone, fore).position.copy(e);
    const cuff = M(new THREE.CylinderGeometry(0.03, 0.06, 0.14, 6, 1, true), robeIn, arm);
    cuff.position.copy(e).add(V(0, -0.06, -0.01));
  };
  // Long fingers: two bones each, ending in a claw (`spread` fans them, `curl` bends them).
  const fingers = (fore: THREE.Object3D, h: THREE.Vector3, dirs: THREE.Vector3[], len: number, curl: THREE.Vector3): void => {
    M(new THREE.SphereGeometry(0.024, 5, 4), bone, fore).position.copy(h);
    for (const d of dirs) {
      const k1 = h.clone().addScaledVector(d, len);
      const k2 = k1.clone().addScaledVector(d, len * 0.8).add(curl);
      rod(fore, h, k1, 0.009, 0.007, bone, 3);
      rod(fore, k1, k2, 0.007, 0.005, bone, 3);
      const tip = M(new THREE.ConeGeometry(0.006, 0.04, 4), claw, fore);
      const dd = k2.clone().sub(k1).normalize();
      tip.position.copy(k2).addScaledVector(dd, 0.016);
      tip.quaternion.setFromUnitVectors(V(0, 1, 0), dd);
    }
  };
  // The upper pair: held up before the face, the fingers closing up round the orb.
  for (const [arm, fore, s, e, h, k] of [[armLJ, foreLJ, shL, elL, hL, -1], [armRJ, foreRJ, shR, elR, hR, 1]] as const) {
    sleeve(arm.abs, fore.abs, s, e, h);
    fingers(fore.abs, h, [V(k * 0.25, 0.75, 0.6).normalize(), V(k * 0.6, 0.75, -0.1).normalize(), V(k * 0.5, 0.8, 0.35).normalize()], 0.045, V(-k * 0.03, 0.02, 0));
  }
  // The lower pair: hanging out from the robe like a mantis's, long fingers splayed, claws down.
  for (const [arm, fore, s, e, h, k] of [[arm2LJ, fore2LJ, sh2L, el2L, h2L, -1], [arm2RJ, fore2RJ, sh2R, el2R, h2R, 1]] as const) {
    sleeve(arm.abs, fore.abs, s, e, h);
    // Two bones in the long forearm, and a spur of bone thrust up and back from the elbow.
    rod(fore.abs, e.clone().add(V(0, -0.03, -0.025)), h.clone().add(V(0, 0.05, -0.02)), 0.011, 0.008, stained, 3);
    const spur = M(new THREE.ConeGeometry(0.016, 0.15, 4), claw, fore.abs);
    const sd = V(k * 0.35, 0.8, -0.5).normalize();
    spur.position.copy(e).addScaledVector(sd, 0.07);
    spur.quaternion.setFromUnitVectors(V(0, 1, 0), sd);
    fingers(fore.abs, h, [V(k * 0.2, -0.9, 0.5).normalize(), V(k * 0.45, -0.85, 0.3).normalize(), V(k * 0.6, -0.8, 0.0).normalize(), V(k * 0.05, -0.9, 0.45).normalize()], 0.06, V(0, 0, 0.03));
  }

  // The orb: ink-glass round a caged knot of cold light, ink curling inside it and dripping off its foot.
  const ob = orbJ.abs;
  M(new THREE.SphereGeometry(0.1, 10, 7), glass, ob).position.copy(O);
  M(new THREE.IcosahedronGeometry(0.035, 0), light, ob).position.copy(O);
  const sw = M(new THREE.TorusGeometry(0.06, 0.006, 3, 10), light, ob);
  sw.position.copy(O);
  sw.rotation.set(0.9, 0.4, 0);
  for (let i = 0; i < 3; i++) {
    const a = i * 2.1;
    const b = M(new THREE.SphereGeometry(0.022, 4, 3), ink, ob);
    b.position.copy(O).add(V(Math.cos(a) * 0.06, Math.sin(a * 1.7) * 0.03, Math.sin(a) * 0.06));
    b.scale.set(1.4, 0.7, 1);
  }
  rod(ob, V(O.x + 0.01, O.y - 0.095, O.z), V(O.x + 0.012, O.y - 0.15, O.z + 0.005), 0.009, 0.004, ink, 4);
  const od = M(new THREE.SphereGeometry(0.01, 5, 4), ink, ob);
  od.position.set(O.x + 0.012, O.y - 0.19, O.z + 0.005);
  od.scale.y = 1.6;
  // A cold light off the orb on the hands and the robe (the caller decides whether to add it).
  pointLight(c, 0x6fdcff, 0.3, 1.4, O.clone().add(V(0, 0.05, 0.1)));
  const core = glowSprite(ob, LIGHT, 0.2, O.clone(), 0.95, true);
  const aura = glowSprite(ob, '#4fcfff', 0.55, O.clone(), 0.5, true);

  // The ward: a bubble of ink-glass veined with the cold light, split down the middle, built WARD_GROW times
  // smaller around the bone so it hides inside the robe until a clip grows it.
  const wg = G(wardJ.abs);
  wg.scale.setScalar(1 / WARD_GROW);
  wg.position.copy(W).multiplyScalar(1 - 1 / WARD_GROW);
  const RW = ORACLE.wardR;
  const foot = V(W.x, W.y - RW, W.z);
  const wardAJ = joint(wg, foot, 'wardA');
  const wardBJ = joint(wg, foot, 'wardB');
  for (const [j, k] of [[wardAJ, -1], [wardBJ, 1]] as const) {
    const half = M(new THREE.SphereGeometry(RW, 9, 7, k < 0 ? Math.PI * 1.5 : Math.PI * 0.5, Math.PI), glass, j.abs);
    half.position.copy(W);
    // The seam: a jagged crack of light down the split (front on one half, back on the other), and veins across.
    polyline(j.abs, vein(W, RW * 1.003, k < 0 ? 0 : Math.PI, 0.1, Math.PI - 0.1, 7, rng, 0.07), 0.008, light);
    for (let v = 0; v < 2; v++) polyline(j.abs, vein(W, RW * 1.004, k * (0.8 + v * 1.4) + R() * 0.3, 0.4 + rng() * 0.2, 1.8 + rng() * 0.5, 4, rng, 0.25), 0.006, light);
  }

  // Only the flicker here: the bob, the halo's turning, the orb's lift and the ward are the clips'.
  const t = (): void => {
    core.material.opacity = 0.8 + rng() * 0.2;
    aura.material.opacity = 0.4 + rng() * 0.2;
  };
  t();
  return done(c, 2.25, t, undefined, bonesOf(c, {
    body: bodyJ.p, robe: robeJ.p, chest: chestJ.p, head: headJ.p, halo: haloJ.p,
    armL: armLJ.p, foreL: foreLJ.p, armR: armRJ.p, foreR: foreRJ.p,
    arm2L: arm2LJ.p, fore2L: fore2LJ.p, arm2R: arm2RJ.p, fore2R: fore2RJ.p,
    drip: drips[0]!.p, drip2: drips[1]!.p, orb: orbJ.p, ward: wardJ.p, wardA: wardAJ.p, wardB: wardBJ.p,
  }));
};
